import { Anchor, Button, Container, createTheme, InputWrapper, Paper, Text, Textarea, TextProps } from '@mantine/core';
import classes from './mantineTheme.module.css';

type CustomTextVariant = 'header' | 'section' | 'caption' | 'timestamp' | 'label';
type AllTextVariants = CustomTextVariant | TextProps['variant'];

export const theme = createTheme({
  fontFamily: 'ABC Diatype',
  headings: {
    fontFamily: 'inherit',
    fontWeight: 'bold',
    sizes: {
      h1: { fontSize: '24px', lineHeight: '1.5' }, // Title
      h2: { fontSize: '20px', lineHeight: '1.25' }, // Section
      h3: { fontSize: '18px', lineHeight: '1.25' }, // Subsection
      h4: { fontSize: '16px', lineHeight: '1.125' }, // Card header
      h5: { fontSize: '14px', lineHeight: '1.125' }, // Inline group title
      h6: { fontSize: '12px', lineHeight: '1.0' }, // Tiny labels
    },
  },
  components: {
    Paper: Paper.extend({
      defaultProps: {
        radius: 'md',
      },
    }),
    Button: Button.extend({
      defaultProps: {
        color: 'deepBlue',
      },
      classNames: classes,
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        color: 'deepBlue',
      },
    }),
    Text: Text.extend({
      defaultProps: {
        size: 'sm',
        style: {
          wordBreak: 'break-word',
        },
      },
      classNames: (_theme, props) => {
        const variant = props.variant as AllTextVariants;

        const variantClasses: Record<CustomTextVariant, string> = {
          header: classes.textHeader,
          section: classes.textSection,
          label: classes.textLabel,
          caption: classes.textCaption,
          timestamp: classes.textTimestamp,
        };

        return {
          root:
            variant && (variant as CustomTextVariant) in variantClasses
              ? variantClasses[variant as CustomTextVariant]
              : '',
        };
      },
    }),
    InputWrapper: InputWrapper.extend({
      styles: (theme) => ({
        label: {
          marginBottom: theme.spacing.xs,
        },
      }),
    }),
    Container: Container.extend({
      defaultProps: {
        fluid: true,
      },
      styles: {
        root: {
          transition: 'width 0.1s linear',
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        autosize: true,
        minRows: 3,
      },
    }),
  },
  colors: {
    deepBlue: [
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
      '#2C51A3',
    ],
    slateBlue: [
      '#f1f7ff',
      '#c8e0ee',
      '#b2cad8',
      '#9db4c1',
      '#879fac',
      '#738a96',
      '#575582',
      '#4b616e',
      '#223843',
      '#1c2f38',
    ],
    blue: [
      '#effbfc',
      '#d7f4f6',
      '#b4e8ed',
      '#80d7e0',
      '#44bccc',
      '#299fb1',
      '#258195',
      '#246a7a',
      '#255765',
      '#234956',
    ],
    gold: [
      '#fdf9ef',
      '#faf2da',
      '#f4e1b4',
      '#efd395',
      '#c6a467',
      '#b18b43',
      '#8d6e35',
      '#785930',
      '#66492d',
      '#583f2b',
    ],
    beige: [
      '#f8f7f4',
      '#fefde5',
      '#fdf9c9',
      '#d6c6b1',
      '#ba8a6a',
      '#a18b6c',
      '#867258',
      '#7c6450',
      '#675345',
      '#554539',
    ],
    gray: [
      '#f8faff',
      '#f1f5f9',
      '#e2e8f0',
      '#cbd5e1',
      '#94a3b8',
      '#64748b',
      '#475569',
      '#334155',
      '#1e293b',
      '#0f172a',
    ],
    red: ['#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'],
    green: [
      '#ecfdf5',
      '#d1fae5',
      '#a7f3d0',
      '#6ee7b7',
      '#34d399',
      '#10b981',
      '#059669',
      '#047857',
      '#065f46',
      '#375462',
    ],
  },
  fontSizes: {
    xxxs: '0.5rem', // 8px
    xxs: '0.625rem', // 10px
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px - default
    md: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.75rem', // 28px
  },

  spacing: {
    xxs: '0.25rem', // 4px
    xs: '0.5rem', // 8px
    sm: '0.75rem', // 12px
    md: '1rem', // 16px
    ms: '1.25rem', // 20px
    lg: '1.5rem', // 24px
    xlg: '1.875rem', // 30px
    xl: '2rem', // 32px
  },
});
